import { useEffect, useState } from "react";
import { useAuth } from "@/lib/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Navigate } from "react-router-dom";
import { KeyRound, Building2, Mail, User } from "lucide-react";
import GenderField from "@/components/GenderField";
import { formatDisplayName } from "@/lib/names";

const PLAN_LABELS = { tier_1: "Tier 1", tier_2: "Tier 2", tier_3: "Tier 3" };

function ChangePasswordCard() {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 8) return toast.error("New password must be at least 8 characters.");
    if (form.new_password !== form.confirm_password) return toast.error("New passwords don't match.");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: form.current_password, new_password: form.new_password });
      toast.success("Password changed. Use the new password on your next login.");
      setForm({ current_password: "", new_password: "", confirm_password: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't change password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card-surface p-6 lg:p-8" data-testid="crm-change-password-card">
      <h2 className="font-serif-display text-2xl flex items-center gap-2" style={{ color: "var(--ink)" }}>
        <KeyRound size={18} style={{ color: "var(--ember)" }} /> Change password
      </h2>
      <p className="text-sm mt-1 mb-5" style={{ color: "var(--muted)" }}>Update the temporary password your admin sent you.</p>
      <form onSubmit={submit} className="space-y-4 max-w-md">
        <div className="space-y-1">
          <label className="block text-[0.6rem] uppercase tracking-[0.18em]" style={{ color: "var(--muted)" }}>Current password</label>
          <input required type="password" value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} data-testid="crm-cp-current" className="w-full px-3 py-2 text-sm border" style={{ borderColor: "var(--border-strong)" }} />
        </div>
        <div className="space-y-1">
          <label className="block text-[0.6rem] uppercase tracking-[0.18em]" style={{ color: "var(--muted)" }}>New password</label>
          <input required type="password" value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} data-testid="crm-cp-new" className="w-full px-3 py-2 text-sm border" style={{ borderColor: "var(--border-strong)" }} />
          <p className="text-[0.65rem]" style={{ color: "var(--muted)" }}>At least 8 characters.</p>
        </div>
        <div className="space-y-1">
          <label className="block text-[0.6rem] uppercase tracking-[0.18em]" style={{ color: "var(--muted)" }}>Confirm new password</label>
          <input required type="password" value={form.confirm_password} onChange={(e) => setForm({ ...form, confirm_password: e.target.value })} data-testid="crm-cp-confirm" className="w-full px-3 py-2 text-sm border" style={{ borderColor: "var(--border-strong)" }} />
        </div>
        <button type="submit" disabled={busy} className="btn-primary" data-testid="crm-cp-submit">
          {busy ? "Saving…" : "Update password"}
        </button>
      </form>
    </div>
  );
}

export default function CrmAccount() {
  const { user, loading, refresh } = useAuth();
  const [org, setOrg] = useState(null);
  const [orgLoading, setOrgLoading] = useState(true);
  const [gender, setGender] = useState(user?.gender || null);
  const [savingGender, setSavingGender] = useState(false);

  useEffect(() => {
    if (!user) return;
    setGender(user.gender || null);
    api.get("/coord/my-org").then((r) => setOrg(r.data)).catch(() => setOrg(null)).finally(() => setOrgLoading(false));
  }, [user]);

  const saveGender = async (next) => {
    setGender(next);
    setSavingGender(true);
    try {
      await api.put("/auth/me", { gender: next });
      toast.success(next === "male" ? "Saved — you'll appear as Bro." : next === "female" ? "Saved — you'll appear as Sis." : "Cleared.");
      refresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save.");
      setGender(user?.gender || null);
    } finally {
      setSavingGender(false);
    }
  };

  if (loading) return <div className="max-w-4xl mx-auto px-6 py-16" style={{ color: "var(--muted)" }}>Loading…</div>;
  if (!user) return <Navigate to="/crm/login?next=/crm/account" replace />;
  if (!user.is_admin && !user.is_org_admin && !user.is_coordinator) return <Navigate to="/crm/login" replace />;

  const preview = formatDisplayName({ first_name: (user.name || "").split(" ")[0] || user.name || "", last_name: (user.name || "").split(" ").slice(1).join(" "), gender });

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-12 py-12 space-y-8" data-testid="crm-account-page">
      <header>
        <div className="overline mb-2">Coordinator portal</div>
        <h1 className="font-serif-display text-4xl tracking-tight" style={{ color: "var(--ink)" }}>Your account</h1>
        <p className="mt-3 text-sm max-w-2xl" style={{ color: "var(--ink-2)" }}>
          Manage your login and view the organization you're linked to.
        </p>
      </header>

      <div className="card-surface p-6 lg:p-8" data-testid="crm-account-info-card">
        <h2 className="font-serif-display text-2xl flex items-center gap-2" style={{ color: "var(--ink)" }}>
          <User size={18} style={{ color: "var(--ember)" }} /> Profile
        </h2>
        <dl className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <dt className="text-[0.6rem] uppercase tracking-[0.18em]" style={{ color: "var(--muted)" }}>Name</dt>
            <dd className="mt-1" style={{ color: "var(--ink)" }}>{user.name || "—"}</dd>
          </div>
          <div>
            <dt className="text-[0.6rem] uppercase tracking-[0.18em]" style={{ color: "var(--muted)" }}>Email</dt>
            <dd className="mt-1 flex items-center gap-2" style={{ color: "var(--ink)" }}><Mail size={14} style={{ color: "var(--muted)" }} /> {user.email}</dd>
          </div>
          <div>
            <dt className="text-[0.6rem] uppercase tracking-[0.18em]" style={{ color: "var(--muted)" }}>Role</dt>
            <dd className="mt-1" style={{ color: "var(--ink)" }}>{user.is_admin ? "Site admin" : user.is_org_admin ? "Organization admin" : "Coordinator"}</dd>
          </div>
        </dl>

        <div className="mt-6 pt-6 border-t" style={{ borderColor: "var(--border-subtle)" }} data-testid="crm-account-gender-block">
          <GenderField value={gender} onChange={saveGender} testid="crm-account-gender" label="Title prefix" />
          <p className="text-xs mt-2" style={{ color: "var(--muted)" }}>
            Picking Male or Female shows your name as <strong>{preview}</strong> across the app (guest cards, pre-service text splits, etc.). {savingGender && <span>Saving…</span>}
          </p>
        </div>
      </div>

      <div className="card-surface p-6 lg:p-8" data-testid="crm-org-info-card">
        <h2 className="font-serif-display text-2xl flex items-center gap-2" style={{ color: "var(--ink)" }}>
          <Building2 size={18} style={{ color: "var(--ember)" }} /> Organization
        </h2>
        {orgLoading ? (
          <p className="mt-4 text-sm" style={{ color: "var(--muted)" }}>Loading…</p>
        ) : !org ? (
          <p className="mt-4 text-sm italic" style={{ color: "var(--muted)" }}>No organization linked. Ask your site admin.</p>
        ) : (
          <dl className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <dt className="text-[0.6rem] uppercase tracking-[0.18em]" style={{ color: "var(--muted)" }}>Church</dt>
              <dd className="mt-1 font-serif-display text-lg" style={{ color: "var(--ink)" }}>{org.name}</dd>
            </div>
            {org.city && (
              <div>
                <dt className="text-[0.6rem] uppercase tracking-[0.18em]" style={{ color: "var(--muted)" }}>City / Area</dt>
                <dd className="mt-1" style={{ color: "var(--ink)" }}>{org.city}</dd>
              </div>
            )}
            {org.contact_email && (
              <div>
                <dt className="text-[0.6rem] uppercase tracking-[0.18em]" style={{ color: "var(--muted)" }}>Church contact</dt>
                <dd className="mt-1" style={{ color: "var(--ink)" }}>{org.contact_email}</dd>
              </div>
            )}
            <div>
              <dt className="text-[0.6rem] uppercase tracking-[0.18em]" style={{ color: "var(--muted)" }}>Plan</dt>
              <dd className="mt-1" style={{ color: "var(--ink)" }}>{PLAN_LABELS[org.plan_tier] || org.plan_tier}</dd>
            </div>
            <div>
              <dt className="text-[0.6rem] uppercase tracking-[0.18em]" style={{ color: "var(--muted)" }}>Coordinator seats</dt>
              <dd className="mt-1" style={{ color: "var(--ink)" }}>Up to {org.max_coordinators}</dd>
            </div>
            <div>
              <dt className="text-[0.6rem] uppercase tracking-[0.18em]" style={{ color: "var(--muted)" }}>Teacher seats</dt>
              <dd className="mt-1" style={{ color: "var(--ink)" }}>Up to {org.max_teachers ?? "—"}</dd>
            </div>
          </dl>
        )}
      </div>

      <ChangePasswordCard />
    </div>
  );
}
